import { usePlanPicker } from "@/hooks/usePlanPicker";
import { useSubscriptionAbility } from "@/hooks/useSubscriptionAbility";
import { useApolloClient, useMutation } from "@apollo/client/react";
import MontuToastProvider from "@repo/components/src/globalProviders/toast";
import {
  Button,
  ButtonSpinner,
  ButtonText,
} from "@repo/components/src/ui/button";
import { Text } from "@repo/components/src/ui/text";
import { Toast, ToastDescription } from "@repo/components/src/ui/toast";
import { PublishCampaignDraftDocument } from "@repo/graphql/documents/campaignDraft/mutation.generated";
import { MyUserWorkspaceDocument } from "@repo/graphql/documents/workspace/query.generated";
import { SubscriptionAbilities } from "@repo/graphql/gql/graphql";
import { useRouter } from "expo-router";
import React, { useCallback } from "react";

type Props = {
  brandId: string;
  /** The chat the card lives in; a plan checkout lands back on it. */
  sessionId: string | undefined;
  draftId: string;
  /** The chat's route is confirming a plan checkout (`useCheckoutReturn`). */
  verifyingPayment: boolean;
};

/**
 * The draft card's "Publicar campaña" for a signed-in brand.
 *
 * Publishing needs a plan with `create_campaign`. Without one the button
 * becomes "Elegir plan": the plan picker opens over the chat and its checkout
 * lands back on this chat (`?sessionId=…&checkout=…`), where the route's
 * `useCheckoutReturn` confirms the payment and refetches the abilities that
 * turn "Publicar campaña" back on. The backend refuses the publish too (a
 * `plan` user error), which opens the same picker.
 *
 * Its own component because every hook here needs a workspace: a guest chat
 * (`/start`) has none and never mounts it.
 */
const showPublishError = (message: string) => {
  MontuToastProvider.show({
    placement: "bottom",
    duration: 4000,
    render: ({ id }) => (
      <Toast nativeID={`toast-${id}`} action="error" variant="solid">
        <ToastDescription>{message}</ToastDescription>
      </Toast>
    ),
  });
};

const PUBLISH_FAILED = "No pudimos publicar la campaña. Intenta de nuevo.";

const PublishDraftButton = React.memo(
  ({ brandId, sessionId, draftId, verifyingPayment }: Props) => {
    const router = useRouter();
    const client = useApolloClient();
    const { can: canCreateCampaign } = useSubscriptionAbility(
      SubscriptionAbilities.CreateCampaign,
    );
    const openPlanPicker = usePlanPicker();
    const choosePlan = useCallback(() => {
      const chatPath = `/app/${brandId}/campaign/ai`;
      openPlanPicker(
        sessionId
          ? `${chatPath}?${new URLSearchParams({ sessionId }).toString()}`
          : chatPath,
      );
    }, [openPlanPicker, brandId, sessionId]);
    const [publish, { loading: publishing }] = useMutation(
      PublishCampaignDraftDocument,
    );

    const onPublish = useCallback(async () => {
      let result: Awaited<ReturnType<typeof publish>>["data"];
      try {
        ({ data: result } = await publish({ variables: { draftId } }));
      } catch {
        showPublishError(PUBLISH_FAILED);
        return;
      }
      const payload = result?.campaignDraft.publishDraft;
      const userError = payload?.userErrors?.[0];
      if (userError?.field?.includes("plan")) {
        // The cached abilities said yes; refresh them so the card switches to
        // "Elegir plan" instead of offering the same refused publish again.
        client.refetchQueries({ include: [MyUserWorkspaceDocument] });
        choosePlan();
        return;
      }
      if (userError || !payload?.campaign?.id) {
        showPublishError(userError?.message ?? PUBLISH_FAILED);
        return;
      }
      router.push({
        pathname: "/app/(brands)/[brandId]/(main)/campaign/[campaignId]",
        params: { brandId, campaignId: payload.campaign.id },
      });
    }, [publish, draftId, brandId, router, client, choosePlan]);

    if (verifyingPayment) {
      return (
        <Button
          action="brand"
          variant="solid"
          size="md"
          className="mt-3 w-full"
          isDisabled
        >
          <ButtonSpinner />
          <ButtonText>Confirmando tu pago…</ButtonText>
        </Button>
      );
    }
    if (canCreateCampaign === false) {
      return (
        <>
          <Text className="mt-3 text-sm text-typography-600">
            Tu borrador está guardado. Para publicarlo necesitas un plan activo.
          </Text>
          <Button
            action="brand"
            variant="solid"
            size="md"
            className="mt-3 w-full"
            onPress={choosePlan}
          >
            <ButtonText>Elegir plan</ButtonText>
          </Button>
        </>
      );
    }
    return (
      <Button
        action="brand"
        variant="solid"
        size="md"
        className="mt-3 w-full"
        isDisabled={publishing}
        onPress={onPublish}
      >
        {publishing ? <ButtonSpinner /> : null}
        <ButtonText>
          {publishing ? "Publicando…" : "Publicar campaña"}
        </ButtonText>
      </Button>
    );
  },
);

PublishDraftButton.displayName = "PublishDraftButton";

export default PublishDraftButton;
